import React, { useEffect, useMemo, useState } from "react";

/**
 * Guided If/Then condition builder.
 *
 * Three-part composer:
 *    [source path]  [operator]  [compare value]
 * Plus a free-text "Advanced" mode for power users (mirrors the raw expression).
 *
 * The output expression matches the runtime evaluator in
 * `/app/backend/flow_runtime.py:evaluate_condition` — supports
 * `==, !=, >, <, >=, <=, in, not in`. Quoted strings, raw numbers,
 * `true`/`false`, lists like `[1, 2]`, and templated paths like
 * `{{steps.foo.output.x}}` are passed through verbatim.
 *
 * Props:
 *  - value: string (current expression) — may be empty
 *  - onChange: (expression: string) => void
 *  - upstreamNodes: Array<{ id, name, type }>
 *  - placeholder, label, testIdPrefix
 */

const OPERATORS = [
    { op: "==", label: "equals" },
    { op: "!=", label: "not equals" },
    { op: ">", label: "greater than" },
    { op: "<", label: "less than" },
    { op: ">=", label: "greater or equal" },
    { op: "<=", label: "less or equal" },
    { op: "in", label: "in (substring / list)" },
    { op: "not in", label: "not in" },
];

// Parse "<source> <op> <value>" — first matching operator from longest to shortest.
function parseExpression(raw) {
    if (!raw) return { source: "", op: "==", value: "" };
    const trimmed = raw.trim();
    const sortedOps = [...OPERATORS].sort((a, b) => b.op.length - a.op.length);
    for (const { op } of sortedOps) {
        // Use a token boundary so "in" doesn't match "include".
        const isWord = /[a-z]/.test(op);
        const re = new RegExp(`^(.*?)\\s+${op.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")}${isWord ? "\\b" : ""}\\s+(.*)$`);
        const m = trimmed.match(re);
        if (m) {
            return { source: m[1].trim(), op, value: m[2].trim() };
        }
    }
    return { source: trimmed, op: "==", value: "" };
}

function buildExpression({ source, op, value }) {
    const s = (source || "").trim();
    const v = (value || "").trim();
    if (!s) return "";
    if (!v && !["==", "!="].includes(op)) return s; // partial — surface raw source
    return `${s} ${op} ${v}`.trim();
}

// Common output suggestions for known node types — purely best-guess hints.
const OUTPUT_HINTS_BY_TYPE = {
    condition: ["pass", "branch"],
    agent: ["text", "json"],
    api: ["status_code", "ok", "body", "body_text"],
    webhook: ["status_code", "ok", "body"],
    transform: [],
    protocol_intel: ["anchorVisit", "visits", "inclusion", "exclusion", "cohorts", "scheduleOfEvents"],
    manual_approval: ["decision", "reason", "approver_email"],
};

export default function ConditionBuilder({
    value,
    onChange,
    upstreamNodes = [],
    placeholder = "{{steps.x.output.status}} == 'ok'",
    testIdPrefix = "cond-builder",
    showAdvanced = true,
}) {
    const initial = useMemo(() => parseExpression(value || ""), [value]);
    const [source, setSource] = useState(initial.source);
    const [op, setOp] = useState(initial.op);
    const [val, setVal] = useState(initial.value);
    const [advanced, setAdvanced] = useState(false);

    // Re-sync from external value when it changes (e.g. switching nodes).
    useEffect(() => {
        const p = parseExpression(value || "");
        setSource(p.source);
        setOp(p.op);
        setVal(p.value);
    }, [value]);

    const sourceSuggestions = useMemo(() => {
        const out = [];
        // Flow-level inputs / meta — generic hints
        out.push("{{input.protocol_id}}");
        out.push("{{input.client_id}}");
        out.push("{{meta.client_id}}");
        // Per-upstream-node outputs (with type-specific hints)
        for (const n of upstreamNodes) {
            const id = (n.id || "").slice(0, 8);
            const hints = OUTPUT_HINTS_BY_TYPE[n.type] || [];
            if (hints.length === 0) {
                out.push(`{{steps.${n.id}.output}}`);
            } else {
                for (const h of hints) {
                    out.push(`{{steps.${n.id}.output.${h}}}`);
                }
            }
            // Display label for datalist option
            // We can't customise <option> in <datalist> hover — but the value
            // itself is verbose enough.
        }
        return out;
    }, [upstreamNodes]);

    const valueSuggestions = useMemo(() => {
        const samples = ["'ok'", "'failed'", "true", "false", "0", "1", "'then'", "'else'"];
        return samples;
    }, []);

    const datalistId = `${testIdPrefix}-source-datalist`;
    const valueListId = `${testIdPrefix}-value-datalist`;

    const composedExpr = buildExpression({ source, op, value: val });

    // When the user edits ANY of the 3 parts, propagate up.
    const propagate = (next) => {
        const expr = buildExpression(next);
        onChange(expr);
    };

    return (
        <div className="space-y-2" data-testid={testIdPrefix}>
            {!advanced && (
                <>
                    <div className="grid grid-cols-[1fr_auto_1fr] gap-1.5 items-start">
                        <input
                            value={source}
                            onChange={(e) => { setSource(e.target.value); propagate({ source: e.target.value, op, value: val }); }}
                            placeholder="{{steps.classify.output.status}}"
                            list={datalistId}
                            data-testid={`${testIdPrefix}-source`}
                            className="input text-[10px] font-mono"
                        />
                        <select
                            value={op}
                            onChange={(e) => { setOp(e.target.value); propagate({ source, op: e.target.value, value: val }); }}
                            data-testid={`${testIdPrefix}-op`}
                            className="input text-[10px]"
                            style={{ minWidth: 86 }}
                        >
                            {OPERATORS.map((o) => (
                                <option key={o.op} value={o.op} title={o.label}>{o.op}</option>
                            ))}
                        </select>
                        <input
                            value={val}
                            onChange={(e) => { setVal(e.target.value); propagate({ source, op, value: e.target.value }); }}
                            placeholder="'ok'"
                            list={valueListId}
                            data-testid={`${testIdPrefix}-value`}
                            className="input text-[10px] font-mono"
                        />
                        <datalist id={datalistId}>
                            {sourceSuggestions.map((s, i) => <option key={i} value={s} />)}
                        </datalist>
                        <datalist id={valueListId}>
                            {valueSuggestions.map((s, i) => <option key={i} value={s} />)}
                        </datalist>
                    </div>
                    <div className="text-[10px] text-white/40 font-mono break-all">
                        Expression: <span className="text-[#7C3AED]">{composedExpr || "(empty)"}</span>
                    </div>
                </>
            )}
            {advanced && (
                <input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={`${testIdPrefix}-advanced`}
                    className="input text-[10px] font-mono"
                />
            )}
            {showAdvanced && (
                <button
                    type="button"
                    onClick={() => setAdvanced((a) => !a)}
                    data-testid={`${testIdPrefix}-toggle-advanced`}
                    className="text-[10px] text-white/40 hover:text-white/70 underline"
                >
                    {advanced ? "Use guided builder" : "Advanced (free-text expression)"}
                </button>
            )}
        </div>
    );
}
